import { type ReactNode } from 'react';
import {
  ChevronDown,
  CircleAlert,
  CircleCheck,
  CircleDashed,
  X,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

/**
 * `error` means the step blocks save; `incomplete` means it is unfinished but
 * harmless, which is the normal resting state for an optional step.
 */
export type RuleStepStatus = 'complete' | 'incomplete' | 'error';

const STATUS_ICON: Record<
  RuleStepStatus,
  { Icon: typeof CircleCheck; className: string; label: string }
> = {
  complete: {
    Icon: CircleCheck,
    className: 'tw:text-emerald-500',
    label: 'Configured',
  },
  incomplete: {
    Icon: CircleDashed,
    className: 'tw:text-muted-foreground',
    label: 'Needs attention',
  },
  error: {
    Icon: CircleAlert,
    className: 'tw:text-destructive',
    label: 'Has an error',
  },
};

export type RuleStepProps = {
  /** 1-based position shown in the rail badge. */
  index: number;
  title: string;
  /** One-line recap shown while collapsed. */
  summary: string;
  status: RuleStepStatus;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Hides the rail connector on the final step. */
  isLast?: boolean;
  /** Renders a remove control; only supplied for optional steps. */
  onRemove?: () => void;
  removeLabel?: string;
  /** Spotlight target for the Event Settings product tour. */
  tour?: string;
  children: ReactNode;
};

export function RuleStep({
  index,
  title,
  summary,
  status,
  open,
  onOpenChange,
  isLast = false,
  onRemove,
  removeLabel,
  tour,
  children,
}: RuleStepProps) {
  const {
    Icon,
    className: statusClassName,
    label: statusLabel,
  } = STATUS_ICON[status];

  return (
    <div className="tw:flex tw:gap-3" data-slot="rule-step" data-tour={tour}>
      <div className="tw:flex tw:w-6 tw:shrink-0 tw:flex-col tw:items-center">
        <Badge
          variant={status === 'error' ? 'destructive' : 'default'}
          className="tw:h-6 tw:w-6 tw:shrink-0 tw:rounded-full tw:px-0 tw:tabular-nums"
          aria-hidden
        >
          {index}
        </Badge>
        {!isLast ? (
          <div className="tw:mt-1 tw:min-h-4 tw:w-px tw:flex-1 tw:bg-border" />
        ) : null}
      </div>

      <Collapsible
        open={open}
        onOpenChange={onOpenChange}
        className={cn(
          'tw:mb-2 tw:min-w-0 tw:flex-1 tw:rounded-lg tw:border tw:bg-card',
          status === 'error' ? 'tw:border-destructive/50' : 'tw:border-border'
        )}
      >
        <div className="tw:flex tw:items-start tw:gap-1 tw:p-2.5">
          <CollapsibleTrigger
            render={
              <button
                type="button"
                className="tw:flex tw:min-w-0 tw:flex-1 tw:cursor-pointer tw:items-start tw:gap-2 tw:border-0 tw:bg-transparent tw:p-0 tw:text-left"
              />
            }
          >
            <Icon
              className={cn(
                'tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0',
                statusClassName
              )}
              aria-label={statusLabel}
            />
            <span className="tw:min-w-0 tw:flex-1">
              <span className="tw:block tw:text-sm tw:font-semibold tw:text-foreground">
                {title}
              </span>
              {!open ? (
                <span className="tw:block tw:truncate tw:text-xs tw:text-muted-foreground">
                  {summary}
                </span>
              ) : null}
            </span>
            <ChevronDown
              className={cn(
                'tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:text-muted-foreground tw:transition-transform',
                open && 'tw:rotate-180'
              )}
              aria-hidden
            />
          </CollapsibleTrigger>

          {onRemove ? (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="tw:h-6 tw:w-6 tw:shrink-0"
              aria-label={removeLabel ?? `Remove ${title}`}
              onClick={onRemove}
            >
              <X className="tw:h-3.5 tw:w-3.5" />
            </Button>
          ) : null}
        </div>

        <CollapsibleContent>
          <div className="tw:space-y-3 tw:border-t tw:border-border tw:p-3">
            {children}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

/** Step list item for the rule editor. Stays outside the scrolling form. */
export function RuleStepNav({
  index,
  title,
  summary,
  status,
  selected,
  onSelect,
}: {
  index: number;
  title: string;
  summary: string;
  status: RuleStepStatus;
  selected: boolean;
  onSelect: () => void;
}) {
  const {
    Icon,
    className: statusClassName,
    label: statusLabel,
  } = STATUS_ICON[status];

  return (
    <button
      type="button"
      aria-current={selected ? 'step' : undefined}
      onClick={onSelect}
      className={cn(
        'tw:flex tw:w-full tw:items-start tw:gap-2 tw:rounded-md tw:border tw:px-2 tw:py-2 tw:text-left',
        selected
          ? 'tw:border-border tw:bg-card'
          : 'tw:border-transparent tw:hover:bg-muted/50'
      )}
    >
      <Badge
        variant={status === 'error' ? 'destructive' : 'default'}
        className="tw:h-6 tw:w-6 tw:shrink-0 tw:rounded-full tw:px-0 tw:tabular-nums"
        aria-hidden
      >
        {index}
      </Badge>
      <span className="tw:min-w-0 tw:flex-1">
        <span className="tw:flex tw:min-w-0 tw:items-start tw:gap-1">
          <Icon
            className={cn('tw:h-3.5 tw:w-3.5 tw:shrink-0', statusClassName)}
            aria-label={statusLabel}
          />
          <span className="tw:min-w-0 tw:text-sm tw:font-semibold tw:whitespace-normal tw:text-foreground">
            {title}
          </span>
        </span>
        <span className="tw:block tw:text-xs tw:whitespace-normal tw:text-muted-foreground">
          {summary}
        </span>
      </span>
    </button>
  );
}
